import { BeatSequencer } from '@/story/sequencer';
import { CULL_CORRIDOR_BEAT, CULL_CUES, CULL_FLAGS, CULL_IDS } from '@/story/act1/cullCorridor';
import { CULL, CULL_BAY, CULL_BIN_AT, CULL_SPAWN, cullBinInterior } from '@/world/cullCorridor';
import { CullDirector, type CullDebug } from '@/world/cullDirector';
import type { Interactable } from '@/systems/interaction';
import type { BeatPartCapabilities, BeatPartLoop, BeatPartModule, BeatPartModuleDefinition } from '@/game/beatPartModule';

/**
 * Act 1 beat 5, *The Cull Corridor*: the bay she walks past, the bin she gets
 * into, and the ride out.
 *
 * Until session 85 this was a branch in `main.ts` plus lines in seven other
 * places there (D-233). Where each piece now runs, against where it ran:
 *
 * - The director's step, the carry and `enterBin` ran right after the
 *   controller; they still do, in `afterMove`, so the interaction system
 *   samples her where the bin put her.
 * - The flags ran there too; they now run in `fixedStep`, after interaction
 *   events, still before `story.update` in the same tick. Each reads director
 *   state `afterMove` settled; a nudge's `tipOver` only starts the topple, and
 *   `binDown` is true only once a later `step` has finished it.
 * - The bin nudge (topple, disable) ran in the interaction-event loop;
 *   `fixedStep` reads it as this tick's edge.
 * - The castors loop was the host's; it is the part's.
 */
function createCullCorridorPartModule(capabilities: BeatPartCapabilities): BeatPartModule {
  const world = capabilities.world;
  if (world === undefined) throw new Error('beat 5 / part 1: the host offered no world capability');
  const parts = world.room.cullCorridor;
  if (parts === undefined) throw new Error('beat 5 / part 1: the room offers no cull corridor seam — booted outside Act 1\'s corridor');

  /** The bin's castors while the ride is moving. See `onCastors`. */
  let castors: BeatPartLoop | null = null;
  const cull = new CullDirector(world.scene, parts, {
    // The room's pulse, at the bay and not at the player. It is the only thing
    // in this beat with a period, and it is deliberately the one thing in it
    // that never reacts to her.
    onPlantCycle: (at) => world.playOneShot('plant-cycle', { gain: 0.9, position: at }),
    onSheeting: (at) => world.playOneShot('sheeting-rustle', { gain: 0.5, position: at }),
    onFootfall: (at) => world.playOneShot('footfall-concrete', { gain: 0.5, position: at, detune: 70 }),
    onLid: (at) => world.playOneShot('bin-lid-close', { gain: 0.8, position: at }),
    // The bin going over. Same registered sound as the lid, pitched down and
    // played harder — see `CullDirectorOptions.onBinFall` for why this beat
    // does not spend a new one on it.
    onBinFall: (at) => world.playOneShot('bin-lid-close', { gain: 1, position: at, detune: -260 }),
    // Rate follows speed, because the bin accelerates and the ear knows what a
    // castor does when it does. A loop, and this is the only place its handle
    // is touched; a one-shot per revolution would be a metronome under a shot
    // that is supposed to feel like being moved.
    onCastors: (speed, at) => {
      if (speed <= 0.001) {
        castors?.stop(0.35);
        castors = null;
        return;
      }
      // `!active` and not `??=`: the engine hands back a permanently inactive
      // handle before it is unlocked, and latching one is the defect an
      // independent review found in beat 4's pant loop.
      if (castors === null || !castors.active) castors = world.playLoop('bin-castors', { gain: 0, position: at });
      castors.setGain(0.55, 0.4);
      castors.setPosition(at);
      castors.setPlaybackRate(0.75 + speed * 0.4, 0.2);
    },
  });
  const story = new BeatSequencer(CULL_CORRIDOR_BEAT);
  const interactables: readonly Interactable[] = [
    // The bay, from the corridor, under `observe`. Six metres of reach because
    // she is meant to look at it from where she is walking rather than have to
    // stand in a spot — and it is the only interactable in Act 1 that is never
    // required by anything.
    { id: CULL_IDS.bay, at: { x: CULL.maxX + 1.0, y: 1.3, z: 0 }, verbs: ['observe'], mapGlyph: 'hook', reach: 6, duration: 3, enabled: false },
    // The bin, under `nudge`, held. `duration` is what makes it a decision:
    // one tap is a pig bumping into a bin, and two and a half seconds of
    // pushing is her getting into one.
    { id: CULL_IDS.bin, at: { x: CULL_BIN_AT.x, y: 0.5, z: CULL_BIN_AT.z }, verbs: ['nudge'], reach: 1.0, duration: 2.5, enabled: false },
  ];
  let disposed = false;

  return {
    story,
    interactables,
    get fade(): number { return cull.fade; },
    // Beat 5 takes the lens for the ride and never gives it back, so both of
    // its suppressors are here: the held camera and the fade (the legacy terms).
    get promptSuppressed(): boolean { return !disposed && (cull.cameraHeld || cull.fade > 0.5); },
    // Its shot always cut, and an eased version of the same shot is a different shot.
    framingCuts: true,
    framing: () => (disposed ? null : cull.framing()),
    afterMove({ dt, player, moveSelf }): void {
      cull.step(dt);
      // ── Being carried ──────────────────────────────────────────────────────
      //
      // The one new mechanic in beat 5: her body is put where the bin is, every
      // fixed step, by the same reset beat 4's capture uses. No "carried" mode
      // in the controller, no parenting of a physics body, no joint — the
      // simulation simply stops being the thing that decides where she is.
      // `reset` and not a velocity: a kinematic character pushed by a moving
      // collider is a physics problem with a whole literature, and the ride is
      // seen from a fixed authored framing.
      //
      // **The pin is on `inside`, from the tick after she is in** — not the
      // righting, which is when the lid shuts and the world takes her. The
      // backstop puts her in from wherever she stood, and a pin that waited for
      // the man left her there for his eleven-second walk (`CullDirector.carrying`).
      if (cull.carrying) moveSelf?.(cull.carryPose);
      // ── The way in is a place rather than a verb ──────────────────────────
      //
      // The topple has to have FINISHED, and she has to be inside the volume,
      // before the bin takes her — both things the world has done rather than
      // things the player asked for (D-090). `cullBinInterior` tracks `binZ`
      // rather than baking the bin's start, so the volume cannot come adrift
      // of the bin the way `CULL_RAIL.y` did from its rail.
      if (cull.binDown && !cull.inside) {
        const inside = cullBinInterior(cull.binZ);
        if (
          player.x >= inside.min.x && player.x <= inside.max.x
          && player.y >= inside.min.y && player.y <= inside.max.y
          && player.z >= inside.min.z && player.z <= inside.max.z
        ) cull.enterBin();
      }
    },
    fixedStep({ player, raiseFlag, nudged }): void {
      // The hold completed and the bin goes over. **Nothing happens to her.**
      // Getting in is a second thing, done on her own feet.
      if (nudged?.(CULL_IDS.bin) === true) {
        cull.tipOver();
        world.interaction.setEnabled(CULL_IDS.bin, false);
      }
      if (cull.binDown) raiseFlag(CULL_FLAGS.binDown);
      if (cull.inside) raiseFlag(CULL_FLAGS.inTheBin);
      if (player.z >= CULL_SPAWN.z + 2.5) raiseFlag(CULL_FLAGS.inCorridor);
      // Level with the mouth, and past it: half-metre bands around the bay's
      // own extents rather than literals, so moving the sheeting moves both.
      if (player.z >= CULL_BAY.mouthMinZ - 0.5) raiseFlag(CULL_FLAGS.atTheBay);
      if (player.z >= CULL_BAY.mouthMaxZ + 2.0) raiseFlag(CULL_FLAGS.pastTheBay);
      if (cull.throughTheDoor) raiseFlag(CULL_FLAGS.throughTheDoor);
    },
    handleCue(cue): void {
      cull.handle(cue as Parameters<CullDirector['handle']>[0]);
      // The bay is offered once and never taken away — she can look at it from
      // anywhere for the rest of the beat, including from inside the bin, and a
      // player who looks back at it while being wheeled past has had the best
      // idea anybody in this project has had.
      if (cue.id === CULL_CUES.offerBay) world.interaction.setEnabled(CULL_IDS.bay, true);
      if (cue.id === CULL_CUES.offerBin) world.interaction.setEnabled(CULL_IDS.bin, true);
    },
    renderStep(frameDt): void {
      if (disposed) return;
      cull.update(frameDt);
    },
    diagnostics(): { readonly kind: 'cull'; readonly cull: CullDebug } {
      return { kind: 'cull', cull: { ...cull.debug, fade: cull.fade } };
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      castors?.stop();
      castors = null;
      cull.dispose();
    },
  };
}

export const CULL_CORRIDOR_PART_MODULE: BeatPartModuleDefinition = {
  beat: 5,
  part: 1,
  room: 'cull',
  create: createCullCorridorPartModule,
};
